/**
 * Split a unified patch into files and lines so the Git tab can paint
 * additions, deletions, and hunk headers instead of dumping a raw pre.
 */

export type DiffLineKind = 'hunk' | 'add' | 'del' | 'ctx' | 'meta';

export type ParsedDiffLine = {
  kind: DiffLineKind;
  text: string;
};

export type ParsedDiffFile = {
  path: string;
  origPath?: string;
  status?: 'added' | 'deleted' | 'renamed';
  lines: ParsedDiffLine[];
};

export function parseUnifiedDiff(patch: string): ParsedDiffFile[] {
  const files: ParsedDiffFile[] = [];
  let current: ParsedDiffFile | null = null;

  const ensureFile = (path = '(patch)'): ParsedDiffFile => {
    if (current) return current;
    current = { path, lines: [] };
    files.push(current);
    return current;
  };

  for (const raw of patch.split('\n')) {
    if (raw.startsWith('diff --git ')) {
      const paths = parseGitPaths(raw.slice('diff --git '.length));
      const added = isNullPath(paths.oldPath);
      const deleted = isNullPath(paths.newPath);
      current = {
        path: deleted ? paths.oldPath : paths.newPath,
        origPath: !added && !deleted && paths.oldPath !== paths.newPath ? paths.oldPath : undefined,
        status: added ? 'added' : deleted ? 'deleted' : paths.oldPath !== paths.newPath ? 'renamed' : undefined,
        lines: [],
      };
      files.push(current);
      continue;
    }

    if (!current && (raw === '' || raw.startsWith('…'))) continue;

    if (/^new file mode /.test(raw)) {
      ensureFile().status = 'added';
      continue;
    }
    if (/^deleted file mode /.test(raw)) {
      ensureFile().status = 'deleted';
      continue;
    }
    if (
      /^(index |old mode |new mode |similarity index |dissimilarity index |rename from |rename to |copy from |copy to )/.test(
        raw,
      )
    ) {
      continue;
    }
    if (/^(--- |\+\+\+ )/.test(raw) && (!current || current.lines.every((line) => line.kind !== 'hunk'))) {
      applyFileHeader(ensureFile(), raw);
      continue;
    }
    if (raw.startsWith('@@')) {
      ensureFile().lines.push({ kind: 'hunk', text: raw });
      continue;
    }
    if (raw.startsWith('Binary files ') || raw.startsWith('GIT binary patch')) {
      ensureFile().lines.push({ kind: 'meta', text: raw });
      continue;
    }
    if (raw.startsWith('+')) {
      ensureFile().lines.push({ kind: 'add', text: raw.slice(1) });
      continue;
    }
    if (raw.startsWith('-')) {
      ensureFile().lines.push({ kind: 'del', text: raw.slice(1) });
      continue;
    }
    if (raw.startsWith('\\') || raw.startsWith('…')) {
      ensureFile().lines.push({ kind: 'meta', text: raw });
      continue;
    }
    if (raw.startsWith(' ')) {
      ensureFile().lines.push({ kind: 'ctx', text: raw.slice(1) });
      continue;
    }
    if (raw === '') continue;
    ensureFile().lines.push({ kind: 'meta', text: raw });
  }

  return files.filter((file) => file.lines.length > 0 || file.path !== '(patch)');
}

function applyFileHeader(file: ParsedDiffFile, raw: string): void {
  const marker = raw.slice(0, 4);
  const spec = raw.slice(4).trim();
  if (spec === '/dev/null') {
    file.status = marker === '--- ' ? 'added' : 'deleted';
    return;
  }
  const path = stripDiffPrefix(spec);
  if (!path) return;
  if (marker === '+++ ') file.path = path;
  else if (marker === '--- ' && path !== file.path) file.origPath = path;
}

function isNullPath(path: string): boolean {
  return path === '/dev/null' || path.toLowerCase() === 'nul';
}

function parseGitPaths(rest: string): { oldPath: string; newPath: string } {
  const match = /^a\/(.+) b\/(.+)$/.exec(rest);
  if (!match?.[1] || !match[2]) return { oldPath: rest, newPath: rest };
  return {
    oldPath: stripDiffPrefix(unquotePath(match[1])),
    newPath: stripDiffPrefix(unquotePath(match[2])),
  };
}

function stripDiffPrefix(path: string): string {
  if (path.startsWith('a/') || path.startsWith('b/')) return path.slice(2);
  return path;
}

function unquotePath(path: string): string {
  if (path.length >= 2 && path.startsWith('"') && path.endsWith('"')) {
    return path.slice(1, -1).replace(/\\([n"\\])/g, (_, ch: string) => (ch === 'n' ? '\n' : ch));
  }
  return path;
}
